import { API_ENDPOINTS } from '@/api/endpoints';
import dropDownIcon from '@/assets/icons/drop-down-icon.png';
import { Notification } from '@/components/Notification';
import { useNotification } from '@/hooks/useNotification';
import { OrganizationPerms } from '@/organization/useOrganizationRole';
import { UserPreferences } from '@/organization/useUserPreferences';
import { Project } from '@/stores/ProjectStore';
import { apiFetch } from '@/util/apiClient';
import { MoreVert, PushPin, PushPinOutlined } from '@mui/icons-material';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { UseMutationResult, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getMainStatusStyle,
  getProjectStatus,
  getSecondaryStatusStyle,
  getTimeAgoDisplay,
} from '../statusUtils';
import { ProjectCardDialogs } from './projectCard/ProjectCardDialogs';
import { ProjectCardMenus } from './projectCard/ProjectCardMenus';
import { SubprojectItem } from './projectCard/SubprojectItem';
import { SubprojectsDisplay } from './projectCard/SubprojectsDisplay';
import { ProjectWithSubprojects, Subproject } from './projectCard/types';
import { getOverallStatus } from './projectCard/utils';

interface ProjectCardProps {
  project: ProjectWithSubprojects;
  allProjects: ProjectWithSubprojects[];
  isPinned: boolean;
  isOpen: boolean;
  onTogglePin: (projectId: string) => void;
  onToggleDropdown: (projectId: string) => void;
  onMenuClick: (
    projectId: string,
    event: React.MouseEvent<HTMLElement>,
  ) => void;
  onClick: () => void;
  bgColor: string;
  hoverColor: string;
  borderStyle: string;
  isSmallScreen: boolean;
  perms?: OrganizationPerms;
  orgId?: string;
  onArchive?: (projectId: string) => void;
  onRestore?: (projectId: string) => void;
  onDuplicate?: (project: Project) => void;
  onDelete?: (projectId: string) => void;
  onEdit?: (project: Project) => void;
  onRefresh?: () => void;
  userPrefs?: UserPreferences;
  updatePrefs?: UseMutationResult<
    UserPreferences,
    Error,
    Partial<UserPreferences>,
    unknown
  >;
}

export function ProjectCard({
  project,
  allProjects,
  isPinned,
  isOpen,
  onTogglePin,
  onToggleDropdown,
  onClick,
  bgColor,
  hoverColor,
  isSmallScreen,
  perms,
  orgId,
  onArchive,
  onRestore,
  onDuplicate: _onDuplicate,
  onDelete,
  onEdit,
  onRefresh: _onRefresh,
  userPrefs,
  updatePrefs,
}: ProjectCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notification, showError, hideNotification } = useNotification();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [subprojectMenuAnchor, setSubprojectMenuAnchor] = useState<{
    anchor: HTMLElement;
    subprojectId: string;
  } | null>(null);
  const [pencilsDownDialogOpen, setPencilsDownDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteArchivedDialogOpen, setDeleteArchivedDialogOpen] =
    useState(false);
  const [deleteSubprojectDialogOpen, setDeleteSubprojectDialogOpen] =
    useState(false);
  const [subprojectToDelete, setSubprojectToDelete] =
    useState<Subproject | null>(null);

  // Get pinned subprojects from backend
  const pinnedSubprojects = useMemo(() => {
    return new Set(userPrefs?.pinnedSubprojects || []);
  }, [userPrefs]);

  // Get original index for status determination
  const originalIndex = allProjects.findIndex((p) => p.id === project.id);

  // Check if all subprojects are completed
  const allSubprojectsCompleted =
    project.subprojects &&
    project.subprojects.length > 0 &&
    project.subprojects.every((sp) => getOverallStatus(sp) === 'finished');

  // Check if project is archived
  const isArchived = !!project.deletedAt;

  // Get project status
  const { mainStatus, secondaryStatus } = getProjectStatus(project);

  const isPencilsDown =
    mainStatus === 'Pencils Down' ||
    mainStatus === 'PM Preferred' ||
    mainStatus === 'BO Approved';
  const mainStatusStyle = getMainStatusStyle(mainStatus);
  const secondaryStatusStyle = secondaryStatus
    ? getSecondaryStatusStyle(secondaryStatus)
    : null;

  // Get time ago display
  const timeAgo = getTimeAgoDisplay(originalIndex);

  // Sort subprojects for expanded cards: pinned status first, then by overall status
  const subprojects = useMemo(() => {
    const actualSubprojects = project.subprojects || [];
    const statusOrder = { finished: 0, 'in-progress': 1, 'not-started': 2 };
    return [...actualSubprojects].sort((a, b) => {
      // First sort by pinned status (pinned items first)
      const aIsPinned = pinnedSubprojects.has(a.id);
      const bIsPinned = pinnedSubprojects.has(b.id);
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;

      // Then sort by overall status
      const aOverallStatus = getOverallStatus(a);
      const bOverallStatus = getOverallStatus(b);
      return statusOrder[aOverallStatus] - statusOrder[bOverallStatus];
    });
  }, [project.subprojects, pinnedSubprojects]);

  // Sort subprojects for status icons: by completion status only
  const subprojectsByCompletion = useMemo(() => {
    const actualSubprojects = project.subprojects || [];
    const statusOrder = { finished: 0, 'in-progress': 1, 'not-started': 2 };
    return [...actualSubprojects].sort((a, b) => {
      const aOverallStatus = getOverallStatus(a);
      const bOverallStatus = getOverallStatus(b);
      return statusOrder[aOverallStatus] - statusOrder[bOverallStatus];
    });
  }, [project.subprojects]);

  // Determine button text based on status
  const getActionButtonText = () => {
    // If project is marked as Completed, show report
    if (mainStatus === 'Completed') {
      return 'View Project Report';
    }

    // Check if project has subprojects
    const hasSubprojects = subprojects && subprojects.length > 0;

    if (hasSubprojects) {
      // Check if at least one subproject has enough data to move to decarb flow
      const subprojectComplete = subprojects.some(
        (sub) => sub.name !== '' && sub.id !== '' && !!sub.projectShape,
      );

      // move to decarb if project has at least 1 subproject and a name
      // in future, (once roles are made) require a program manager, and a budget officer:
      // project.programManagers &&
      // project.programManagers.length > 0 &&
      // project.budgetOfficers &&
      // project.budgetOfficers.length > 0
      if (subprojectComplete && project.name !== '') {
        return 'Go to Decarb Hub';
      }
    }

    // Default: project has no subprojects or has incomplete subprojects
    return 'Go To Project Details';
  };

  const handlePencilsDown = async () => {
    setPencilsDownDialogOpen(false);
    if (!orgId) return;
    try {
      await apiFetch(
        API_ENDPOINTS.ORGANIZATIONS.PROJECTS.PENCILS_DOWN(orgId, project.id),
        { method: 'POST' },
      );
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      await queryClient.invalidateQueries({
        queryKey: ['projects-with-subprojects'],
      });
      const firstSubproject = project.subprojects?.[0];
      const targetSubId = firstSubproject?.id || 'overview';
      navigate(`/project/${project.id}/decarb/${targetSubId}`);
    } catch (error) {
      console.error('Failed to set Pencils Down:', error);
      showError('Failed to set Pencils Down status.');
    }
  };

  const handleDeleteProject = async () => {
    setDeleteDialogOpen(false);
    // Hard delete the project
    if (orgId && perms?.canEditOrgSettings) {
      try {
        await apiFetch(
          API_ENDPOINTS.ORGANIZATIONS.PROJECTS.DETAIL(orgId, project.id),
          {
            method: 'DELETE',
          },
        );
        // Invalidate all project-related queries to trigger refetch
        await queryClient.invalidateQueries({
          queryKey: ['projects'],
        });
        await queryClient.invalidateQueries({
          queryKey: ['projects-with-subprojects'],
        });
      } catch (error) {
        console.error('Failed to delete project:', error);
      }
    }
  };

  const handleDeleteArchivedProject = async () => {
    setDeleteArchivedDialogOpen(false);
    // Call the onDelete handler from LandingPage
    onDelete?.(project.id);
  };

  const handleDeleteSubproject = async () => {
    setDeleteSubprojectDialogOpen(false);
    if (!subprojectToDelete || !orgId) return;

    try {
      await apiFetch(
        API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DETAIL(
          orgId,
          subprojectToDelete.id,
        ),
        {
          method: 'DELETE',
        },
      );
      // Invalidate all project-related queries to trigger refetch
      await queryClient.invalidateQueries({
        queryKey: ['projects'],
      });
      await queryClient.invalidateQueries({
        queryKey: ['projects-with-subprojects'],
      });
    } catch (error) {
      console.error('Failed to delete subproject:', error);
    }
    setSubprojectToDelete(null);
  };

  // Helper function to refresh project data after mutations
  const refreshProjectData = async () => {
    // Use resetQueries to clear cache AND trigger fresh fetch
    await Promise.all([
      queryClient.resetQueries({ queryKey: ['projects-with-subprojects'] }),
      queryClient.resetQueries({ queryKey: ['projects'] }),
    ]);
  };

  const handleDuplicateSubproject = async (subprojectId: string) => {
    if (!orgId) return;

    try {
      await apiFetch(
        API_ENDPOINTS.ORGANIZATIONS.SUBPROJECTS.DUPLICATE(orgId, subprojectId),
        {
          method: 'POST',
        },
      );

      await refreshProjectData();
    } catch (error: any) {
      console.error('Failed to duplicate subproject:', error);

      // Extract error message from API response
      const errorMessage =
        error?.detail ||
        error?.message ||
        'Failed to duplicate subproject. Please try again.';
      showError(errorMessage);
    }
  };

  return (
    <React.Fragment key={project.id}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          px: 'clamp(12px, 1.04vw, 20px)',
          height: {
            xs: 'clamp(45px, 5vh, 55px)',
            lg: 'clamp(55px, 6.5vh, 70px)',
            xl: 'clamp(65px, 7vh, 70px)',
          },
          minHeight: {
            xs: 'clamp(40px, 2.5vw, 48px)',
            lg: 'clamp(48px, 3vw, 58px)',
            xl: 'clamp(54px, 3.3vw, 64px)',
          },
          flexShrink: 0,
          backgroundColor: `${bgColor} !important`,
          border: 'none',
          borderRadius: 0,
          transition: 'all 0.2s',
          backgroundImage: 'none !important',
          boxShadow: 'none !important',
          '&:hover': {
            backgroundColor: `${hoverColor} !important`,
          },
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          {/* Gap before buttons */}
          <Box sx={{ width: 'clamp(7px, 0.42vw, 8px)', flexShrink: 0 }} />

          {/* Pin and Menu buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(1px, 0.16vw, 3px)',
              width: 'clamp(36px, 2.24vw, 43px)',
              flexShrink: 0,
            }}
          >
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                onTogglePin(project.id);
              }}
              sx={{ p: 'clamp(3px, 0.26vw, 5px)' }}
            >
              {isPinned ? (
                <PushPin
                  sx={{
                    fontSize: 'clamp(15px, 0.94vw, 18px)',
                    color: isPencilsDown ? '#FFFFFF' : '#000000',
                  }}
                />
              ) : (
                <PushPinOutlined
                  sx={{
                    fontSize: 'clamp(15px, 0.94vw, 18px)',
                    color: isPencilsDown
                      ? 'rgba(255, 255, 255, 0.6)'
                      : 'rgba(0, 0, 0, 0.6)',
                  }}
                />
              )}
            </IconButton>
            <IconButton
              size='small'
              onClick={(e) => {
                e.stopPropagation();
                // Toggle menu: close if already open, open if closed
                setMenuAnchor(menuAnchor ? null : e.currentTarget);
              }}
              sx={{ p: 'clamp(3px, 0.26vw, 5px)' }}
            >
              <MoreVert
                sx={{
                  fontSize: 'clamp(15px, 0.94vw, 18px)',
                  color: isPencilsDown
                    ? 'rgba(255, 255, 255, 0.6)'
                    : 'rgba(0, 0, 0, 0.6)',
                }}
              />
            </IconButton>
          </Box>

          {/* Gap after buttons before title */}
          <Box sx={{ width: 'clamp(48px, 3.33vw, 64px)', flexShrink: 0 }} />

          {/* Project name */}
          <Box
            sx={{
              width: isSmallScreen ? '20%' : '15%',
              flexShrink: 0,
              minWidth: 'clamp(100px, 6.25vw, 120px)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 700,
                fontSize: 'clamp(10px, 0.73vw, 14px)',
                color: isPencilsDown ? '#FFFFFF' : 'rgba(19, 54, 86, 1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {project.name}
            </Typography>
          </Box>

          {/* Gap between name and description - hide on small screens */}
          {!isSmallScreen && (
            <Box sx={{ width: 'clamp(3px, 1.82vw, 35px)', flexShrink: 0 }} />
          )}

          {/* Project description - hide on small screens */}
          {!isSmallScreen && (
            <Box
              sx={{
                width: {
                  xs: '7%',
                  sm: '8%',
                  md: '9%',
                  lg: '10%',
                  xl: '13%',
                  '@media (min-width: 1920px)': {
                    width: '16%',
                  },
                },
                flexShrink: 0,
                minWidth: {
                  xs: 'clamp(25px, 4vw, 75px)',
                  md: 'clamp(30px, 4.5vw, 90px)',
                  lg: 'clamp(30px, 4.8vw, 100px)',
                  xl: 'clamp(35px, 6vw, 130px)',
                  '@media (min-width: 1920px)': {
                    minWidth: 'clamp(50px, 7vw, 150px)',
                  },
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                  fontSize: 'clamp(7px, 0.625vw, 12px)',
                  color: isPencilsDown ? '#FFFFFF' : 'rgba(19, 54, 86, 1)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {project.description}
              </Typography>
            </Box>
          )}

          {/* Gap between description and status */}
          <Box
            sx={{
              width: isSmallScreen
                ? 'clamp(10px, 4vw, 60px)'
                : 'clamp(2px, 2.5vw, 48px)',
              flexShrink: 0,
            }}
          />

          {/* Status columns */}
          <Box
            sx={{
              display: 'flex',
              gap: 'clamp(6px, 0.52vw, 10px)',
              width: '18%',
              flexShrink: 0,
              minWidth: 'clamp(125px, 7.81vw, 150px)',
              flexWrap: 'nowrap',
            }}
          >
            {/* Secondary Status - Right aligned */}
            <Box
              sx={{
                width: '50%',
                display: 'flex',
                justifyContent: 'flex-end',
                flexShrink: 0,
              }}
            >
              {secondaryStatusStyle &&
                mainStatus !== 'Completed' &&
                mainStatus !== 'Archived' && (
                  <Box
                    sx={{
                      backgroundColor: secondaryStatusStyle.bg,
                      borderRadius: 'clamp(3px, 0.21vw, 4px)',
                      px: 'clamp(4px, 0.31vw, 6px)',
                      py: 'clamp(1px, 0.1vw, 2px)',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: 'clamp(10px, 0.625vw, 12px)',
                        color: secondaryStatusStyle.color,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {secondaryStatus}
                    </Typography>
                  </Box>
                )}
            </Box>

            {/* Main Status - Left aligned */}
            <Box
              sx={{
                width: '50%',
                display: 'flex',
                justifyContent: 'flex-start',
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  backgroundColor: mainStatusStyle.bg,
                  borderRadius: 'clamp(3px, 0.21vw, 4px)',
                  px: 'clamp(4px, 0.31vw, 6px)',
                  py: 'clamp(1px, 0.1vw, 2px)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: 'clamp(10px, 0.625vw, 12px)',
                    color: mainStatusStyle.color,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {mainStatus}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Gap between status and subprojects */}
          <Box
            sx={{
              width: isSmallScreen
                ? 'clamp(20px, 2.5vw, 40px)'
                : 'clamp(8px, 1vw, 20px)',
              flexShrink: 0,
            }}
          />

          {/* Subprojects column */}
          <Box
            sx={{
              width: '15%',
              flexShrink: 0,
              minWidth: 'clamp(108px, 6.77vw, 130px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'clamp(3px, 0.26vw, 5px)',
              paddingTop: subprojects.length > 6 ? 'clamp(12px, 1vw, 19px)' : 0,
            }}
          >
            <SubprojectsDisplay
              subprojects={subprojectsByCompletion}
              isPencilsDown={isPencilsDown}
              timeAgo={timeAgo}
            />
          </Box>

          {/* Gap before action button - larger on small screens to compensate for hidden description */}
          <Box
            sx={{
              width: isSmallScreen
                ? 'clamp(60px, 15vw, 200px)'
                : 'clamp(50px, 6.5vw, 125px)',
              flexShrink: 0,
            }}
          />

          {/* Action Button and Dropdown */}
          <Box
            sx={{
              width: 'clamp(180px, 11.5vw, 220px)',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: isSmallScreen
                ? '8px'
                : {
                    xs: '6px',
                    sm: '8px',
                    md: '10px',
                    lg: '12px',
                    xl: '14px',
                  },
            }}
          >
            {/* Action Button */}
            <Button
              {...(getActionButtonText() !== 'View Project Report'
                ? getActionButtonText() !== 'Go To Project Details'
                  ? {
                      component: Link,
                      to: `/project/${project.id}/decarb/overview`,
                    }
                  : {
                      component: Link,
                      to: `/project/${project.id}/proj-details`,
                    }
                : {})}
              onClick={(e) => {
                e.stopPropagation();
                const buttonText = getActionButtonText();
                if (buttonText === 'View Project Report') {
                  onClick();
                }
              }}
              disableRipple
              sx={{
                textDecoration: 'none',
                backgroundColor: 'rgba(14, 70, 63, 1) !important',
                color: '#FFFFFF !important',
                fontFamily: 'Inter, sans-serif',
                fontSize: 'clamp(9px, 0.57vw, 11px)',
                fontWeight: 700,
                textTransform: 'none',
                padding: 'clamp(3px, 0.2vw, 4px) clamp(14px, 1.4vw, 27px)',
                borderRadius: 'clamp(2px, 0.21vw, 4px)',
                minWidth: 'clamp(120px, 6.5vw, 125px)',
                width: 'clamp(120px, 6.5vw, 125px)',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(24, 90, 83, 1) !important',
                  boxShadow: 'none',
                },
                '&:active': {
                  backgroundColor: 'rgba(8, 50, 43, 1) !important',
                  boxShadow: 'none',
                },
              }}
            >
              {getActionButtonText()}
            </Button>

            {/* Dropdown Icon */}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleDropdown(project.id);
              }}
              sx={{ p: 1.5 }}
            >
              <Box
                component='img'
                src={dropDownIcon}
                alt='dropdown'
                sx={{
                  width: 'clamp(18px, 1.3vw, 22px)',
                  height: 'clamp(18px, 1.3vw, 22px)',
                  objectFit: 'contain',
                  display: 'block',
                  transform: isOpen ? 'scaleY(-1)' : 'scaleY(1)',
                  transition: 'transform 0.2s',
                  filter: isPencilsDown ? 'brightness(0) invert(1)' : 'none',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Paper>

      {/* Dropdown Content */}
      {isOpen && (
        <Box
          sx={{
            // Outer container with background and padding
            backgroundColor: bgColor,
            borderBottom: '3px solid rgba(0, 61, 54, 1)',
            pl: 'clamp(50px, 3.5vw, 70px)',
            pr: 'clamp(50px, 3.5vw, 70px)', // Right padding - scrollbar will be inside this
            py: 'clamp(16px, 1.25vw, 24px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 'clamp(18px, 1.5vw, 28px)',
              // Height for exactly 2 rows: 2 cards + 1 gap between them
              maxHeight: 'clamp(195px, calc(12.5vh + 1.5vw), 255px)', // Minimized screens
              '@media (min-width: 1025px)': {
                maxHeight: 'clamp(200px, calc(12.5vh + 1.5vw), 260px)', // Laptop screens
              },
              '@media (min-width: 1700px)': {
                maxHeight: 'clamp(225px, calc(14.5vh + 1.5vw), 425px)', // Large monitor screens
              },
              // Only show scrollbar if more than 2 rows worth of content
              overflowY:
                (project.subprojects?.length || 0) >
                (mainStatus === 'Active' ? 7 : 8)
                  ? 'auto'
                  : 'hidden',
            }}
          >
            {/* Go to subproject details */}
            <Box
              component='button'
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/project/${project.id}/proj-details`);
              }}
              sx={{
                appearance: 'none',
                backgroundColor: 'primary.light',
                border: '3px solid rgba(19, 54, 86, 0.2)',
                borderRadius: 'clamp(4px, 0.42vw, 8px)',
                width: 'clamp(170px, 12vw, 240px)',
                minHeight: 'clamp(80px, 6vh, 105px)',
                '@media (min-width: 1025px)': {
                  width: 'clamp(200px, 14vw, 280px)',
                  minHeight: 'clamp(82px, 6vh, 108px)',
                },
                '@media (min-width: 1700px)': {
                  width: 'clamp(230px, 16vw, 310px)',
                  minHeight: 'clamp(95px, 7vh, 190px)',
                },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                px: 1.5,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: 'rgba(19, 54, 86, 0.5)',
                  borderColor: 'rgba(19, 54, 86, 0.35)',
                },
                '&:active': {
                  backgroundColor: 'rgba(19, 54, 86, 0.6)',
                  transform: 'scale(0.98)',
                },
                '&:focus-visible': {
                  outline: '2px solid rgba(19, 54, 86, 0.8)',
                  outlineOffset: '2px',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 700,
                  fontSize: 'clamp(13px, 0.9vw, 16px)',
                  color: 'info.dark',
                  lineHeight: 1.2,
                  textAlign: 'center',
                }}
              >
                Go To Project Details
              </Typography>

              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: 'clamp(10px, 0.65vw, 12px)',
                  color: 'primary.dark',
                  letterSpacing: '-0.011em',
                  textAlign: 'center',
                }}
              >
                Edit project details and create new subprojects
              </Typography>
            </Box>

            {/* Existing Subprojects */}
            {subprojects.map((subproject) => {
              // If parent project is pencils down/archived, override subproject status
              const effectiveStatus =
                mainStatus === 'Pencils Down' ||
                mainStatus === 'PM Preferred' ||
                mainStatus === 'BO Approved' ||
                mainStatus === 'Archived'
                  ? 'pencils-down'
                  : getOverallStatus(subproject);

              return (
                <SubprojectItem
                  key={subproject.id}
                  subproject={subproject}
                  projectId={project.id}
                  isPinned={pinnedSubprojects.has(subproject.id)}
                  effectiveStatus={effectiveStatus}
                  mainStatus={mainStatus}
                  onTogglePin={(subprojectId) => {
                    if (updatePrefs) {
                      const currentPinned = userPrefs?.pinnedSubprojects || [];
                      const newPinned = currentPinned.includes(subprojectId)
                        ? currentPinned.filter((id) => id !== subprojectId)
                        : [...currentPinned, subprojectId];
                      updatePrefs.mutate({ pinnedSubprojects: newPinned });
                    }
                  }}
                  onMenuClick={(e, subprojectId) => {
                    setSubprojectMenuAnchor({
                      anchor: e.currentTarget,
                      subprojectId: subprojectId,
                    });
                  }}
                />
              );
            })}
          </Box>
        </Box>
      )}

      <ProjectCardMenus
        projectMenuAnchor={menuAnchor}
        onProjectMenuClose={(e) => {
          e?.stopPropagation?.();
          setMenuAnchor(null);
        }}
        allSubprojectsCompleted={!!allSubprojectsCompleted}
        isArchived={!!isArchived}
        perms={perms}
        onBOApproval={() => {
          const firstSubproject = project.subprojects?.[0];
          const targetSubId = firstSubproject?.id || 'overview';
          navigate(`/project/${project.id}/decarb/${targetSubId}`);
        }}
        onPencilsDown={() => setPencilsDownDialogOpen(true)}
        onDuplicate={() => _onDuplicate?.(project)}
        onArchive={() => onArchive?.(project.id)}
        onRestore={() => onRestore?.(project.id)}
        onDeleteArchived={() => setDeleteArchivedDialogOpen(true)}
        onEdit={() => onEdit?.(project)}
        onDelete={() => setDeleteDialogOpen(true)}
        subprojectMenuAnchor={subprojectMenuAnchor}
        onSubprojectMenuClose={(e) => {
          e?.stopPropagation?.();
          setSubprojectMenuAnchor(null);
        }}
        mainStatus={mainStatus}
        onEditSubproject={(subprojectId) => {
          navigate(`/project/${project.id}/subproj/${subprojectId}`);
        }}
        onViewSubproject={(subprojectId) => {
          navigate(`/project/${project.id}/decarb/${subprojectId}`);
        }}
        onDuplicateSubproject={handleDuplicateSubproject}
        onDeleteSubproject={(subprojectId) => {
          const subproject = subprojects.find((s) => s.id === subprojectId);
          if (subproject) {
            setSubprojectToDelete(subproject);
            setDeleteSubprojectDialogOpen(true);
          }
        }}
      />

      <ProjectCardDialogs
        pencilsDownDialogOpen={pencilsDownDialogOpen}
        projectName={project.name}
        onPencilsDownConfirm={handlePencilsDown}
        onPencilsDownCancel={() => setPencilsDownDialogOpen(false)}
        deleteDialogOpen={deleteDialogOpen}
        onDeleteProjectConfirm={handleDeleteProject}
        onDeleteProjectCancel={() => setDeleteDialogOpen(false)}
        deleteArchivedDialogOpen={deleteArchivedDialogOpen}
        onDeleteArchivedConfirm={handleDeleteArchivedProject}
        onDeleteArchivedCancel={() => setDeleteArchivedDialogOpen(false)}
        deleteSubprojectDialogOpen={deleteSubprojectDialogOpen}
        subprojectToDelete={subprojectToDelete}
        onDeleteSubprojectConfirm={handleDeleteSubproject}
        onDeleteSubprojectCancel={() => {
          setDeleteSubprojectDialogOpen(false);
          setSubprojectToDelete(null);
        }}
      />

      <Notification notification={notification} onClose={hideNotification} />
    </React.Fragment>
  );
}
